// MaturaTrigTanFromCosClosedTask.js
// Zadanie maturalne – zamknięte (trygonometria):
// Kąt α jest ostry. Dana jest jedna z funkcji trygonometrycznych (sin, cos lub tan),
// a w zadaniu trzeba wyznaczyć inną funkcję (sin, cos albo tan) – odpowiedź ABCD.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";
import TriangleDiagram from "../../../../components/diagrams/TriangleDiagram";


const LETTERS = ["A", "B", "C", "D"];

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const pick = (arr) => arr[randInt(0, arr.length - 1)];

function shuffle(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = randInt(0, i);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

const gcd = (a, b) => {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) {
    [x, y] = [y, x % y];
  }
  return x || 1;
};

function simplifyFraction(num, den) {
  const g = gcd(num, den);
  const s = den < 0 ? -1 : 1;
  const n = (num * s) / g;
  const d = Math.abs(den) / g;
  return { num: n, den: d };
}

const formatFractionLatex = (num, den) => {
  const { num: n, den: d } = simplifyFraction(num, den);
  if (d === 1) {
    return String(n);
  }
  return `\\dfrac{${n}}{${d}}`;
};

// ======================= GENERATOR =======================

function createOriginalTask() {
  const values = [
    { num: 5, den: 12 },
    { num: 12, den: 13 },
    { num: 13, den: 12 },
    { num: 12, den: 5 },
  ];
  const options = values.map(({ num, den }, index) => ({
    num,
    den,
    value: num / den,
    latex: formatFractionLatex(num, den),
    label: LETTERS[index],
    isCorrect: index === 3,
  }));

  return {
    type: "MaturaTrigTanFromCosClosedTask",
    data: {
      a: 5,
      b: 12,
      c: 13,
      given: "cos",
      target: "tan",
      givenFrac: { num: 5, den: 13 },
      targetFrac: { num: 12, den: 5 },
      options,
      correctIndex: 3,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_SIE",
      taskNumber: 19,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") {
    return createOriginalTask();
  }

  // klasyczne trójki pitagorejskie (a – przyprostokątna przy α, b – naprzeciw α, c – przeciwprostokątna)
  const TRIPLES = [
    { a: 3, b: 4, c: 5 },
    { a: 5, b: 12, c: 13 },
    { a: 8, b: 15, c: 17 },
    { a: 7, b: 24, c: 25 },
  ];

  const triple = pick(TRIPLES);
  const { a, b, c } = triple;

  // funkcje dla ostrego kąta α (I ćwiartka)
  const sinFrac = simplifyFraction(b, c);
  const cosFrac = simplifyFraction(a, c);
  const tanFrac = simplifyFraction(b, a);
  const cotFrac = simplifyFraction(a, b); // odwrotność tangensa

  // warianty:
  // 1) dana cos, szukamy tan
  // 2) dana cos, szukamy sin
  // 3) dana sin, szukamy cos
  // 4) dana sin, szukamy tan
  // 5) dana tan, szukamy sin
  // 6) dana tan, szukamy cos
  const VARIANTS = [
    { given: "cos", target: "tan" },
    { given: "cos", target: "sin" },
    { given: "sin", target: "cos" },
    { given: "sin", target: "tan" },
    { given: "tan", target: "sin" },
    { given: "tan", target: "cos" },
  ];

  const { given, target } = pick(VARIANTS);

  const getFrac = (name) => {
    if (name === "sin") return sinFrac;
    if (name === "cos") return cosFrac;
    if (name === "tan") return tanFrac;
    if (name === "cot") return cotFrac;
    throw new Error("Unknown func");
  };

  const givenFrac = getFrac(given);
  const targetFrac = getFrac(target);

  // zmyłki – inne funkcje + odwrotność tangensa
  let distractorsNames;
  if (given === "cos" && target === "tan") {
    distractorsNames = ["cot", "sin", "cos"];
  } else if (given === "cos" && target === "sin") {
    distractorsNames = ["tan", "cot", "cos"];
  } else if (given === "sin" && target === "cos") {
    distractorsNames = ["sin", "tan", "cot"];
  } else if (given === "sin" && target === "tan") {
    distractorsNames = ["cot", "sin", "cos"];
  } else if (given === "tan" && target === "sin") {
    distractorsNames = ["cos", "tan", "cot"];
  } else if (given === "tan" && target === "cos") {
    distractorsNames = ["sin", "tan", "cot"];
  } else {
    distractorsNames = ["sin", "cos", "cot"];
  }

  const optionFracs = [
    { ...targetFrac, tag: "correct" },
    ...distractorsNames.map((name) => ({
      ...getFrac(name),
      tag: name,
    })),
  ];

  const optionsShuffled = shuffle(
    optionFracs.map((fr) => {
      const { num, den } = simplifyFraction(fr.num, fr.den);
      return {
        num,
        den,
        value: num / den,
        latex: formatFractionLatex(num, den),
        isCorrect: fr.tag === "correct",
      };
    })
  );

  const options = optionsShuffled.map((opt, idx) => ({
    ...opt,
    label: LETTERS[idx],
  }));

  const correctIndex = options.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaTrigTanFromCosClosedTask",
    data: {
      a,
      b,
      c,
      given,
      target,
      givenFrac,
      targetFrac,
      options,
      correctIndex,
    },
  };
}

// ======================= RENDER TREŚCI =======================

export function renderTask({ data }) {
  const { given, target, givenFrac, options } = data;

  const givenLatex = formatFractionLatex(givenFrac.num, givenFrac.den);

  let givenLabel;
  let targetLabel;

  if (given === "sin") givenLabel = "\\sin\\alpha";
  else if (given === "cos") givenLabel = "\\cos\\alpha";
  else givenLabel = "\\tan\\alpha";

  if (target === "sin") targetLabel = "\\sin\\alpha";
  else if (target === "cos") targetLabel = "\\cos\\alpha";
  else targetLabel = "\\tan\\alpha";

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>
      <p>
        Kąt <InlineMath math={'\\alpha'} /> jest ostry oraz{" "}
        <InlineMath math={`${givenLabel} = ${givenLatex}`} />.
      </p>
      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>
        Funkcja <InlineMath math={targetLabel} /> jest równa:
      </p>
      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 10 }}>
            <InlineMath
              math={`${opt.label}.\\; ${formatFractionLatex(
                opt.num,
                opt.den
              )}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// ======================= INPUT (ABCD) =======================

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;

  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: formatFractionLatex(opt.num, opt.den),
      value: opt.value,
    });
  };

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

// ======================= SPRAWDZANIE =======================

export function checkAnswer({ data }, userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !Number.isInteger(userAnswer.choiceIndex)
  ) {
    return false;
  }
  return userAnswer.choiceIndex === data.correctIndex;
}

// ======================= POPRAWNA ODPOWIEDŹ =======================

export function renderAnswer({ data }) {
  const { options, correctIndex } = data;
  const correct = options[correctIndex];

  return (
    <InlineMath
      math={`${correct.label}.\\; ${formatFractionLatex(
        correct.num,
        correct.den
      )}`}
    />
  );
}

// ======================= ODPOWIEDŹ UŻYTKOWNIKA =======================

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    userAnswer.latex == null
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, latex } = userAnswer;

  return (
    <div style={{ marginTop: "1rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${label}.\\; ${latex}`} />
    </div>
  );
}

// ======================= WYJAŚNIENIE =======================

export function renderExplanation({ data }) {
  const { a, b, c, given, target, givenFrac, targetFrac } = data;

  const givenLabel =
    given === "sin" ? "\\sin\\alpha" : given === "cos" ? "\\cos\\alpha" : "\\tan\\alpha";
  const targetLabel =
    target === "sin" ? "\\sin\\alpha" : target === "cos" ? "\\cos\\alpha" : "\\tan\\alpha";

  const givenLatex = formatFractionLatex(givenFrac.num, givenFrac.den);
  const targetLatex = formatFractionLatex(targetFrac.num, targetFrac.den);

  // Założenie jak w Twoich zadaniach z PF:
  // α przy wierzchołku A, kąt prosty w C
  // - przyległa do α: a = AB
  // - przeciwległa do α: b = BC
  // - przeciwprostokątna: c = AC
  //
  // (To odpowiada typowym trójkom pitagorejskim używanym w generatorze)

  // Który bok jest "brakujący" i oznaczamy go jako x?
  // - gdy dane sin: znamy b i c, brakuje a
  // - gdy dane cos: znamy a i c, brakuje b
  // - gdy dane tan: znamy b i a, brakuje c
  const xDesc =
    given === "sin"
      ? { what: "przyległa", latexEq: `x^2 + ${b}^2 = ${c}^2`, solve: `x = \\sqrt{${c}^2 - ${b}^2} = ${a}` }
      : given === "cos"
      ? { what: "przeciwległa", latexEq: `${a}^2 + x^2 = ${c}^2`, solve: `x = \\sqrt{${c}^2 - ${a}^2} = ${b}` }
      : { what: "przeciwprostokątna", latexEq: `${a}^2 + ${b}^2 = x^2`, solve: `x = \\sqrt{${a}^2 + ${b}^2} = ${c}` };

  // Definicja funkcji danej (pokazujemy z liczbami z rysunku)
  const givenDef =
    given === "sin"
      ? `\\sin\\alpha = \\dfrac{\\text{przeciwległa}}{\\text{przeciwprostokątna}} = \\dfrac{${b}}{${c}} = ${givenLatex}`
      : given === "cos"
      ? `\\cos\\alpha = \\dfrac{\\text{przyległa}}{\\text{przeciwprostokątna}} = \\dfrac{${a}}{${c}} = ${givenLatex}`
      : `\\tan\\alpha = \\dfrac{\\text{przeciwległa}}{\\text{przyległa}} = \\dfrac{${b}}{${a}} = ${givenLatex}`;

  // Definicja funkcji szukanej (po wyliczeniu x i tak wpisujemy wynik końcowy)
  const targetDef =
    target === "sin"
      ? `\\sin\\alpha = \\dfrac{${b}}{${c}} = ${targetLatex}`
      : target === "cos"
      ? `\\cos\\alpha = \\dfrac{${a}}{${c}} = ${targetLatex}`
      : `\\tan\\alpha = \\dfrac{${b}}{${a}} = ${targetLatex}`;

    const labelSidesOverride =
      given === "sin"
        ? { a: "x" } // brakująca przyległa (a)
        : given === "cos"
        ? { b: "x" } // brakująca przeciwległa (b)
        : { c: "x" }; // brakująca przeciwprostokątna (c)

  const sinLatex = formatFractionLatex(b, c);
  const cosLatex = formatFractionLatex(a, c);
  const tanLatex = formatFractionLatex(b, a);
  const identitySteps = given === "cos"
    ? [
        `\\sin^2\\alpha=1-\\cos^2\\alpha=1-\\left(\\dfrac{${a}}{${c}}\\right)^2=\\dfrac{${c * c - a * a}}{${c * c}}=\\dfrac{${b * b}}{${c * c}}`,
        `\\alpha\\text{ jest ostry, więc }\\sin\\alpha=${sinLatex}`,
      ]
    : given === "sin"
    ? [
        `\\cos^2\\alpha=1-\\sin^2\\alpha=1-\\left(\\dfrac{${b}}{${c}}\\right)^2=\\dfrac{${c * c - b * b}}{${c * c}}=\\dfrac{${a * a}}{${c * c}}`,
        `\\alpha\\text{ jest ostry, więc }\\cos\\alpha=${cosLatex}`,
      ]
    : [
        `\\tan\\alpha=${tanLatex}=\\dfrac{\\sin\\alpha}{\\cos\\alpha}\\quad\\Longrightarrow\\quad\\sin\\alpha=\\dfrac{${b}}{${a}}\\cos\\alpha`,
        `\\left(\\dfrac{${b}}{${a}}\\cos\\alpha\\right)^2+\\cos^2\\alpha=1`,
        `\\cos\\alpha=${cosLatex},\\qquad\\sin\\alpha=${sinLatex}`,
      ];
  const identityTarget = target === "tan"
    ? `\\tan\\alpha=\\dfrac{\\sin\\alpha}{\\cos\\alpha}=\\dfrac{${sinLatex}}{${cosLatex}}=${tanLatex}`
    : target === "sin"
    ? `\\sin\\alpha=${sinLatex}`
    : `\\cos\\alpha=${cosLatex}`;


  return (
    <div className="space-y-4" style={{ textAlign: "left" }}>
      <p>
        <strong>Sposób I. Korzystamy z tożsamości trygonometrycznych.</strong>
      </p>
      <p>
        Zaczynamy od jedynki trygonometrycznej i definicji tangensa:
      </p>
      <BlockMath math={'\\sin^2\\alpha+\\cos^2\\alpha=1'} />
      <BlockMath math={'\\tan\\alpha=\\dfrac{\\sin\\alpha}{\\cos\\alpha}'} />
      {identitySteps.map((line) => <BlockMath key={line} math={line} />)}
      <BlockMath math={identityTarget} />
      <p>
        <strong>Wniosek.</strong>{" "}
        <InlineMath math={`${targetLabel} = ${targetLatex}`} />
        <InlineMath math={'\\text{.}'} />
      </p>

      <details className="matura-alternative-explanation">
        <summary className="matura-alternative-explanation__summary">
          Inny sposób: trójkąt prostokątny
        </summary>
        <div className="matura-alternative-explanation__content space-y-4">
          <p>
            <strong>Krok 1.</strong> Rysujemy trójkąt prostokątny. Brakujący bok
            oznaczamy jako <InlineMath math={'x'} />.
          </p>
          <TriangleDiagram
            a={a}
            b={b}
            c={c}
            angleAt="B"
            showSideLabels
            showBetaAngle={false}
            labelSides={labelSidesOverride}
            alphaLabel="\\alpha"
          />
          <p>
            <strong>Krok 2.</strong> Z treści zadania mamy{" "}
            <InlineMath math={`${givenLabel} = ${givenLatex}`} />, czyli:
          </p>
          <BlockMath math={givenDef} />
          <p>
            Bok <InlineMath math="x" /> ({xDesc.what}) obliczamy z twierdzenia Pitagorasa:
          </p>
          <BlockMath math={xDesc.latexEq} />
          <BlockMath math={xDesc.solve} />
          <p>
            <strong>Krok 3.</strong> Zapisujemy szukaną funkcję dla tego samego trójkąta:
          </p>
          <BlockMath math={targetDef} />
        </div>
      </details>
    </div>
  );
}


